import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { interval, Observable, Subject } from 'rxjs';
import { filter, last, map, mergeMap, take } from 'rxjs/operators';

import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { AlcmeonContextService } from '../../core/alcmeon-context/alcmeon-context.service';
import { AttachmentType, CreateVideoReply, UploadFileResponse, UploadMediaResponse } from '../models/misc.model';
import { API_URL_COMPANIES_V2, API_URL_PREFIX, JSON_CONTENT_TYPE_HEADER } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

export const MESSAGE_PAGE_SIZE = 20;

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  constructor(
    private http: HttpClient,
    private ehs: ErrorHandlingService,
    private alcmeonContext: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService
  ) {}

  // Returns the newly created media id
  uploadMedia(
    blob: Blob,
    type: AttachmentType,
    siteId: number,
    progress: Subject<number>,
    pollMediaUploadedOnSocialNetwork: boolean
  ): Observable<number> {
    const mediaURL = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/sites/${siteId}/medias`;
    const formData = new FormData();
    let mediaType: string = type;
    if (type === AttachmentType.Picture) {
      mediaType = 'image';
    }
    formData.set('media_type', mediaType);
    formData.set('file', blob);
    const req = new HttpRequest('POST', mediaURL, formData, {
      reportProgress: true,
    });
    let result$ = this.http.request<UploadMediaResponse>(req).pipe(
      map(event => this.handleProgressEventForMediaOrFileUpload(event, progress)),
      last(), // return the media_id to the subscriber, skipping all the 'null' returned by handleProgressEventForMediaOrFileUpload()
      this.ehs.catchAlertAndThrowHttpError()
    );
    if (pollMediaUploadedOnSocialNetwork) {
      result$ = result$.pipe(
        mergeMap((mediaId: number) => this.pollObservable(mediaId, 'picture')),
        this.ehs.catchAlertAndThrowHttpError()
      );
    }
    return result$;
  }

  // Hopefully the backend will phase out /medias in favour of this endpoint
  uploadFile(
    blobAndFileName,
    siteId: number,
    progress: Subject<number>,
    pollMediaUploadedOnSocialNetwork: boolean
  ): Observable<number> {
    const mediaURL = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/sites/${siteId}/file-upload`;
    const formData = new FormData();
    formData.set('file_name', blobAndFileName.fileName);
    formData.set('file', blobAndFileName.blob);
    const req = new HttpRequest('POST', mediaURL, formData, {
      reportProgress: true,
    });
    let result$ = this.http.request<UploadFileResponse>(req).pipe(
      map(event => this.handleProgressEventForMediaOrFileUpload(event, progress)),
      last(), // return the file_id to the subscriber, skipping all the 'null' returned by handleProgressEventForMediaOrFileUpload()
      this.ehs.catchAlertAndThrowHttpError()
    );
    if (pollMediaUploadedOnSocialNetwork) {
      result$ = result$.pipe(
        mergeMap((mediaId: number) => this.pollObservable(mediaId, 'file')),
        this.ehs.catchAlertAndThrowHttpError()
      );
    }
    return result$;
  }

  uploadPictureAsync(blob: Blob, siteId: number) {
    const mediaURL = `${API_URL_COMPANIES_V2}/${this.alcmeonContext.companyId}/sites/${siteId}/medias`;
    const formData = new FormData();
    formData.set('media_type', 'image');
    formData.set('file', blob);
    return this.asyncRequestService.post(mediaURL, formData).pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  isMediaUploadedOnSocialNetwork(mediaId, mediaType): Observable<boolean> {
    const companyId = this.alcmeonContext.companyId;
    const url = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/poll-uploaded-media/${mediaId}/media-type/${mediaType}`;
    return this.http.get<any>(url).pipe(map(response => response.isMediaUploaded));
  }

  pollObservable(mediaId, mediaType) {
    return interval(2000).pipe(
      mergeMap(() => this.isMediaUploadedOnSocialNetwork(mediaId, mediaType)),
      filter(Boolean),
      take(1),
      map(() => mediaId)
    );
  }

  pictureUrl(pictureId: number) {
    const companyId = this.alcmeonContext.companyId;
    return `${API_URL_PREFIX}/${companyId}/pictures/${pictureId}`;
  }

  videoUrl(videoId) {
    const companyId = this.alcmeonContext.companyId;
    return `${API_URL_PREFIX}/${companyId}/videos/${videoId}`;
  }

  fileUrl(fileId) {
    const companyId = this.alcmeonContext.companyId;
    return `${API_URL_PREFIX}/api/1.0/companies/${companyId}/file-upload/${fileId}`;
  }

  // Converts a GIF to blob
  convertGifToBlob(gifUrl: string) {
    return this.http.get(gifUrl, { responseType: 'blob' }).pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  // Reports upload progress (from 0 to 1) to the given subject.
  // Returns the media_id found in the response body if the event is the response event,
  // or null otherwise.
  private handleProgressEventForMediaOrFileUpload(
    event: HttpEvent<UploadMediaResponse | UploadFileResponse>,
    progress: Subject<number>
  ) {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        progress.next(event.loaded / event.total);
        return null;
      case HttpEventType.Response:
        return (event.body as UploadMediaResponse).media_id || (event.body as UploadFileResponse).file_id;
      default:
        return null;
    }
  }

  // UPLOAD BY CHUNK

  private getVideoIdFromHttpResponse(event: CreateVideoReply) {
    return event.video_id;
  }

  createVideoInDb(file) {
    const createVideoUrl = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/upload-by-chunk`;
    const videoExtension: string = file.name.split('.')[1];
    const requestBody = {
      type: `video/${videoExtension}`,
      size: file.size,
      name: file.name,
    };
    return this.http.post<CreateVideoReply>(createVideoUrl, requestBody, JSON_CONTENT_TYPE_HEADER).pipe(
      map(event => this.getVideoIdFromHttpResponse(event)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  deleteVideoAndChunks(file, videoId: number) {
    const deleteVideoUrl = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/upload-by-chunk/${videoId}/delete`;
    const requestBody = {
      number_of_uploaded_chunks: file.upload.chunks.length,
    };
    return this.http
      .post<any>(deleteVideoUrl, requestBody, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  reconstructVideo(file, videoId: number) {
    const reconstructVideoUrl = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/upload-by-chunk/${videoId}/reconstruct`;
    return this.http
      .post<any>(reconstructVideoUrl, { number_of_chunks: file.upload.totalChunkCount }, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError());
  }
}
